import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { RouterOutlet, RouterLink, Router } from '@angular/router';
import { UserManagementComponent } from '@components/user-management/user-management.component';
import { AccessControlService } from '@services/access-control.service';
import { ButtonAccess } from '@constants/button-access.constants';
import {
  IconoirStar,
  IconoirGroup,
  IconoirArchive,
  IconoirPageEdit,
  IconoirPcCheck,
  IconoirNetworkReverse,
  IconoirReports,
  IconoirSettings,
  IconoirLifebelt,
  IconoirHelpCircle,
  IconoirPrivacyPolicy,
  IconoirHomeSimple,
  IconoirGraphUp,
  IconoirUpload,
  IconoirUploadSquare,
} from '@ey-xd/motif-icon';
import {
  ModalSizes,
  MotifAvatarModule,
  MotifButtonModule,
  MotifDropdownModule,
  MotifHeaderModule,
  MotifIconModule,
  MotifModal,
  MotifThemeModule,
  MotifTooltipModule,
  MotifVerticalNavigationModule,
} from '@ey-xd/ng-motif';
import {
  TranslatePipe,
  TranslateDirective,
  TranslateService,
} from '@ngx-translate/core';
import { CommonService } from '@services/common.service';
import { SecureStorageService } from '@services/securestorage.service';
import { MasterdataUploadComponent } from '@components/masterdata-upload/masterdata-upload.component';

@Component({
  selector: 'app-side-nav',
  imports: [
    RouterOutlet,
    RouterLink,
    MotifHeaderModule,
    TranslatePipe,
    TranslateDirective,
    MotifThemeModule,
    MotifAvatarModule,
    MotifButtonModule,
    MotifVerticalNavigationModule,
    MotifIconModule,
    CommonModule,
    MotifDropdownModule,
    MotifTooltipModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './side-nav.component.html',
  styleUrl: './side-nav.component.scss',
})
export class SideNavComponent implements OnInit, OnChanges {
  @Input() showMenu: boolean = true;
  @Input() isDashboard = false;
  @Input() menuToggled: boolean = false;

  items = [];
  settingsIcon = IconoirSettings;
  supportIcon = IconoirLifebelt;
  private readonly alwaysEnabledIporRoutes = [
    '',
    'my-workspace/All',
    'my-workspace/Favorites',
    'my-workspace/Archives',
  ];
  disableSettingsIcon = false;
  private readonly alwaysEnabledNonEYUserIporRoute = [''];

  constructor(
    private readonly translate: TranslateService,
    private readonly commonService: CommonService,
    private readonly router: Router,
    public modal: MotifModal,
    private accessControlService: AccessControlService,
    private readonly sessionService: SecureStorageService,
  ) {}

  ngOnInit(): void {
    this.loadSideNavBar();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['menuToggled'] && !changes['menuToggled'].firstChange) {
      this.loadSideNavBar();
    }
  }

  loadMenu(): void {
    this.translate
      .get([
        'Home',
        'Chat',
        'MyWorkSpace',
        'Archives',
        'Favorites',
        'ManageTeams',
        'Reporting',
        'Search',
        'With Custom Template',
        'AdminPanel',
        'Dashboard',
        'OneDatabaseUpload',
      ])
      .subscribe((translations) => {
        this.items = [
          {
            label: translations['Home'],
            icon: IconoirHomeSimple,
            exact: true,
            routerLink: [''],
            roles: ['Viewer', 'Admin', 'Owner', 'Editor'],
            iporPermission: 'Landing Page',
          },
          {
            label: translations['MyWorkSpace'],
            icon: IconoirPageEdit,
            src: 'assets/icons/file-pen 2.svg',
            exact: true,
            routerLink: ['my-workspace/All'],
            roles: ['Viewer', 'Admin', 'Owner', 'Editor'],
            iporPermission: 'View My Projects',
          },
          // {
          //   label: translations['Chat'],
          //   icon: IconoirChatLines,
          //   exact: true,
          //   routerLink: ['']
          // },
          {
            label: translations['Favorites'],
            icon: IconoirStar,
            exact: true,
            routerLink: ['my-workspace/Favorites'],
            roles: ['Viewer', 'Admin', 'Owner', 'Editor'],
            iporPermission: 'View My Projects',
          },
          {
            label: translations['Archives'],
            icon: IconoirArchive,
            exact: true,
            routerLink: ['my-workspace/Archives'],
            roles: ['Viewer', 'Admin', 'Owner', 'Editor'],
            iporPermission: 'View My Projects',
          },
          {
            label: translations['AdminPanel'],
            icon: IconoirPrivacyPolicy,
            exact: true,
            routerLink: ['approval-request'],
            roles: ['SuperAdmin'],
            iporPermission: 'Admin Panel Approvals',
          },
          {
            label: translations['OneDatabaseUpload'],
            icon: IconoirUploadSquare,
            exact: true,
            routerLink: ['onedb-upload'],
            roles: ['SuperAdmin'],
            iporPermission: 'Master Data Upload',
          },
          {
            label: translations['ManageTeams'],
            icon: IconoirGroup,
            exact: true,
            routerLink: ['user-access-management'],
            roles: ['Admin', 'Owner'],
            iporPermission: 'Manage Teams (UAM)',
          },
          {
            label: translations['Dashboard'],
            icon: IconoirGraphUp,
            exact: true,
            roles: ['Viewer', 'Admin', 'Owner', 'Editor'],
            routerLink: ['dashboard'],
            iporPermission: 'Readiness Assessment Dashboard',
          },
        ];
      });
  }

  loadSideNavBar() {
    this.loadMenu();
    let userInfo = this.commonService.getUserInfo();
    if (userInfo?.IsAdmin) {
      return;
    }
    let accessibleUserRolesList = this.commonService.getAccessibleRoles();
    this.setNavigationMenuByRole(this.items, accessibleUserRolesList);
  }

  setNavigationMenuByRole(menuList: any, userRoles: any) {
    if (this.commonService.isIPORModule()) {
      this.items = menuList
        .filter((m) => m.iporPermission != null)
        .map((m) => ({
          ...m,
          disabled: this.alwaysEnabledIporRoutes.includes(m?.routerLink?.[0])
            ? false
            : !this.accessControlService.hasPermission(m.iporPermission),
        }));
      return;
    }
    const roleNames = userRoles.map((role) => role.RoleName);
    this.items = menuList.filter((m) =>
      m.roles.some((role) => roleNames.includes(role)),
    );
    this.disableSettingsIcon = this.isSettingsDisabled();
  }

  trackByLabel(index: number, item: any): string {
    return item.label;
  }

  trackByRouterLink(index: number, subItem: any): string {
    return subItem.routerLink;
  }

  hasAccessMyWorkSpace(): boolean {
    return this.accessControlService.hasAccess(ButtonAccess.MyWorkSpaceAccess);
  }

  private isAlwaysEnabledForIpor(item: any): boolean {
    const route = item?.routerLink?.[0];
    if (this.commonService.isIPORExternalViewerUser()) {
      return this.alwaysEnabledNonEYUserIporRoute.includes(route);
    }
    return this.alwaysEnabledIporRoutes.includes(route);
  }

  isSideNavItemDisabled(item: any): boolean {
    if (item?.disabled) {
      return true;
    }

    if (!this.commonService.isIPORExternalViewerUser()) {
      return false;
    }

    return !this.isAlwaysEnabledForIpor(item);
  }

  isSettingsDisabled(): boolean {
    return this.commonService.isExternalUser();
  }

  menuNavigationHandler(item) {
    if (this.isSideNavItemDisabled(item)) {
      return;
    }

    if (item.label === this.translate.instant('Dashboard')) {
      this.sessionService.removeSessionStorageData('projectId');
    }
    if (
      item.label === this.translate.instant('ManageTeams') &&
      !this.commonService.isIPORModule()
    ) {
      const accessRolesList = this.commonService.getAccessibleRoles();
      const userRoles = accessRolesList.map((role) => role.RoleName);
      const itemRoles = item.roles;
      const hasAccess = itemRoles.some((role) => userRoles.includes(role));
      if (hasAccess) {
        const modalRef = this.modal.open(UserManagementComponent, {
          data: null,
          size: ModalSizes.XL,
          slideout: true,
          disableClose: true,
          minWidth: '30vw',
          maxWidth: '80vw',
          width: '38vw', // Start with Invite tab width
        });
        // Listen for width change event from UserManagementComponent
        if (
          modalRef &&
          modalRef.componentInstance &&
          modalRef.componentInstance.modalWidthChange
        ) {
          modalRef.componentInstance.modalWidthChange.subscribe(
            (width: string) => {
              modalRef.updateSize(width);
            },
          );
        }
      }
    } else if (item.label === this.translate.instant('OneDatabaseUpload')) {
      const userInfo = this.commonService.getUserInfo();
      if (userInfo.IsAdmin) {
        this.modal.open(MasterdataUploadComponent, {
          size: ModalSizes.XL,
          disableClose: true,
          width: '46rem',
        });
      }
    } else {
      this.router.navigateByUrl('/' + item.routerLink[0]);
    }
  }

  settingsNavigationHandler() {
    if (this.isSettingsDisabled()) {
      return;
    }
    this.router.navigateByUrl('/settings');
  }
}
